import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { BaseModalComponent } from "@ha/ui/common";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Actions, ofActionSuccessful, Store } from "@ngxs/store";

import { PwaState } from "@ha/util/pwa";
import { TranslocoService } from "@ngneat/transloco";
import { PermitDetailsActions } from "../../../states/permit-details.action";

export interface PermitCommentCreateComponentData {
    permitId: number;
    step: number;
}

@UntilDestroy()
@Component({
    templateUrl: "permit-comment-create-modal.component.html",
    styleUrls: ["permit-comment-create-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitCommentCreateComponent extends BaseModalComponent implements OnInit, AfterViewInit {
    public data: PermitCommentCreateComponentData;

    public text: string;

    public CreateCommentAction = PermitDetailsActions.CreateComment;

    @ViewChild("comment")
    private commentTextArea: ElementRef<HTMLElement>;

    constructor(private store: Store, private actions$: Actions, private translocoService: TranslocoService) {
        super();
    }

    public ngOnInit() {
        this.actions$.pipe(untilDestroyed(this), ofActionSuccessful(PermitDetailsActions.CreateComment)).subscribe(
            () => {
                this.closeModal();
                window.scrollTo({
                top: 0,
                behavior: "smooth",
                });
            },
        );
    }

    public ngAfterViewInit(): void {
        this.commentTextArea.nativeElement.focus();
        this.setupCommentTextareaListener();
    }

    public createComment() {
        this.store.dispatch(new PermitDetailsActions.CreateComment(this.data.permitId, this.text, this.data.step));
    }

    public close() {
        this.closeModal();
        window.scrollTo({
        top: 0,
        behavior: "smooth",
        });
    }

    private setupCommentTextareaListener(): void {
        if (this.commentTextArea && this.commentTextArea.nativeElement) {
            this.commentTextArea.nativeElement.addEventListener("keydown", (event: KeyboardEvent) => {
                this.onCommentKeydown(event);
            });
        }
    }

    public onCommentKeydown(event: KeyboardEvent): void {
        if (event.key === "Enter") {
            if (event.shiftKey) {
                return;
            } else {
                event.preventDefault();
                this.createComment();
            }
        }
    }

    public getPlaceholder(): string {
        const isMobile = this.store.selectSnapshot(PwaState.isMobile);
        return isMobile
            ? this.translocoService.translate("permit.details.comments.createModal.commentPlaceholderMobile")
            : this.translocoService.translate("permit.details.comments.createModal.commentPlaceholder");
    }
}
